import React, { useState } from 'react';
import { useAuth } from '../services/authContext';
import { apiService } from '../services/apiService';
import { useToast } from '../components/Toast';

const Settings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState({
    firstName: user?.firstName || '',
    lastName:  user?.lastName  || '',
    email:     user?.email     || '',
  });
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [profileMsg, setProfileMsg]   = useState(null);
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [savingProfile, setSavingProfile]   = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const handleProfileSubmit = async e => {
    e.preventDefault();
    try {
      setSavingProfile(true);
      setProfileMsg(null);
      await apiService.update('users', user._id ?? user.id, {
        firstName: profile.firstName,
        lastName:  profile.lastName,
        email:     profile.email,
        role:      user.role,
        active:    true,
      });
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
      toast('Profile updated successfully.', 'success');
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.message || 'Failed to update profile.' });
      toast(err.response?.data?.message || 'Failed to update profile.', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async e => {
    e.preventDefault();
    if (passwords.next !== passwords.confirm) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (passwords.next.length < 6) {
      setPasswordMsg({ type: 'error', text: 'Password must be at least 6 characters.' });
      return;
    }
    try {
      setSavingPassword(true);
      setPasswordMsg(null);
      await apiService.put(`users/${user._id ?? user.id}/change-password`, {
        currentPassword: passwords.current,
        newPassword:     passwords.next,
      });
      setPasswordMsg({ type: 'success', text: 'Password changed successfully.' });
      toast('Password changed successfully.', 'success');
      setPasswords({ current: '', next: '', confirm: '' });
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err.response?.data?.message || 'Failed to change password.' });
      toast(err.response?.data?.message || 'Failed to change password.', 'error');
    } finally {
      setSavingPassword(false);
    }
  };

  if (!user) return null;

  const msgStyle = type => ({
    padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem',
    background: type === 'success' ? '#d4edda' : '#f8d7da',
    color:      type === 'success' ? '#155724' : '#721c24',
  });

  return (
    <div className="page-container">
      <div className="page-header"><h1>Settings</h1></div>

      <div className="settings-container">
        <div className="settings-card">
          <h2>Profile</h2>
          {profileMsg && <div style={msgStyle(profileMsg.type)}>{profileMsg.text}</div>}
          <form onSubmit={handleProfileSubmit} className="form">
            <div className="form-row">
              <div className="form-group">
                <label>First Name</label>
                <input value={profile.firstName} onChange={e => setProfile(p => ({ ...p, firstName: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input value={profile.lastName} onChange={e => setProfile(p => ({ ...p, lastName: e.target.value }))} required />
              </div>
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label>Role</label>
              <input value={user.role.charAt(0).toUpperCase() + user.role.slice(1)} disabled />
            </div>
            <button type="submit" className="btn btn-primary" disabled={savingProfile}>
              {savingProfile ? 'Saving...' : 'Update Profile'}
            </button>
          </form>
        </div>

        <div className="settings-card">
          <h2>Change Password</h2>
          {passwordMsg && <div style={msgStyle(passwordMsg.type)}>{passwordMsg.text}</div>}
          <form onSubmit={handlePasswordSubmit} className="form">
            <div className="form-group">
              <label>Current Password</label>
              <input type="password" value={passwords.current} onChange={e => setPasswords(p => ({ ...p, current: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <input type="password" value={passwords.next} onChange={e => setPasswords(p => ({ ...p, next: e.target.value }))} required minLength={6} />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input type="password" value={passwords.confirm} onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))} required minLength={6} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={savingPassword}>
              {savingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;
